import { StyleSheet, View, ScrollView, Pressable } from "react-native";
import { router } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import Head from "expo-router/head";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useTheme } from "@/hooks/useTheme";
import { useSMS } from "@/hooks/useSMS";
import useAuth from "@/hooks/useAuth";

export default function ChatIndexPage() {
  const { colors } = useTheme();
  const { displayNames } = useAuth();
  const { phones, unreadCounts, totalUnread } = useSMS();

  const handlePhonePress = (phoneNumber: string) => {
    const encodedPhone = encodeURIComponent(phoneNumber);
    router.push(`/chat/sms/${encodedPhone}`);
  };

  // Empty state - no phone numbers assigned to role
  if (phones.length === 0) {
    return (
      <View style={[styles.container, { backgroundColor: colors.backgroundSecondary }]}>
        <Head>
          <title>RockForce {displayNames?.chat?.plural || "Chat"}</title>
          <meta name="description" content="SMS conversations" />
        </Head>

        <View style={styles.emptyState}>
          <MaterialCommunityIcons name="phone-off" size={64} color={colors.textSecondary} />
          <ThemedText style={styles.emptyTitle}>No Phone Numbers</ThemedText>
          <ThemedText style={[styles.emptyDescription, { color: colors.textSecondary }]}>
            No phone numbers are assigned to your role. Contact your administrator for access.
          </ThemedText>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: colors.backgroundSecondary }]}>
      <Head>
        <title>RockForce {displayNames?.chat?.plural || "Chat"}</title>
        <meta name="description" content="SMS conversations" />
      </Head>

      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.header}>
          <ThemedText style={styles.headerTitle}>Select a Phone Number</ThemedText>
          <ThemedText style={[styles.headerSubtitle, { color: colors.textSecondary }]}>
            Choose a number to view and send SMS messages
          </ThemedText>
        </View>

        <View style={styles.phoneList}>
          {phones.map((phone) => {
            const unread = unreadCounts[phone.number] || 0;
            return (
              <Pressable
                key={phone.number}
                onPress={() => handlePhonePress(phone.number)}
                style={({ pressed, hovered }) => [
                  styles.phoneCard,
                  {
                    backgroundColor: colors.card,
                    borderColor: colors.border,
                  },
                  hovered && { backgroundColor: colors.background },
                  pressed && { opacity: 0.7 },
                ]}
              >
                <View style={[styles.phoneIcon, { backgroundColor: colors.primary + "20" }]}>
                  <MaterialCommunityIcons name="phone" size={24} color={colors.primary} />
                </View>
                <View style={styles.phoneInfo}>
                  <ThemedText style={styles.phoneName}>{phone.displayName}</ThemedText>
                  {phone.alias && phone.showNumber ? (
                    <ThemedText style={[styles.phoneNumber, { color: colors.textSecondary }]}>
                      {phone.number}
                    </ThemedText>
                  ) : null}
                </View>
                <View style={styles.phoneRight}>
                  {unread > 0 && (
                    <View style={[styles.badge, { backgroundColor: colors.primary }]}>
                      <ThemedText style={styles.badgeText}>{unread > 99 ? "99+" : unread}</ThemedText>
                    </View>
                  )}
                  <MaterialCommunityIcons name="chevron-right" size={24} color={colors.textSecondary} />
                </View>
              </Pressable>
            );
          })}
        </View>

        {totalUnread > 0 && (
          <View style={[styles.totalUnread, { backgroundColor: colors.primary + "10" }]}>
            <MaterialCommunityIcons name="message-badge" size={20} color={colors.primary} />
            <ThemedText style={[styles.totalUnreadText, { color: colors.primary }]}>
              {totalUnread} unread message{totalUnread > 1 ? "s" : ""} total
            </ThemedText>
          </View>
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 32,
  },
  header: {
    marginBottom: 24,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: "700",
    marginBottom: 4,
  },
  headerSubtitle: {
    fontSize: 14,
  },
  phoneList: {
    gap: 12,
  },
  phoneCard: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    gap: 12,
  },
  phoneIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: "center",
    justifyContent: "center",
  },
  phoneInfo: {
    flex: 1,
  },
  phoneName: {
    fontSize: 16,
    fontWeight: "600",
  },
  phoneNumber: {
    fontSize: 13,
    marginTop: 2,
  },
  phoneRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  badge: {
    minWidth: 24,
    height: 24,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 8,
  },
  badgeText: {
    color: "white",
    fontSize: 12,
    fontWeight: "600",
  },
  emptyState: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    gap: 16,
    paddingHorizontal: 32,
  },
  emptyTitle: {
    fontSize: 24,
    fontWeight: "600",
    marginTop: 16,
  },
  emptyDescription: {
    fontSize: 14,
    textAlign: "center",
    lineHeight: 20,
  },
  totalUnread: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginTop: 16,
    padding: 12,
    borderRadius: 8,
  },
  totalUnreadText: {
    fontSize: 14,
    fontWeight: "500",
  },
});
